/* eslint-disable default-case */
import type { RefObject } from 'react';
import { createRef } from 'react';

import type { Reducer } from '@reduxjs/toolkit';
import { combineReducers } from '@reduxjs/toolkit';
import type { RouterState } from 'connected-react-router';
import { connectRouter, LOCATION_CHANGE } from 'connected-react-router';
import type { Action, History, Location, LocationState } from 'history';
import produce from 'immer';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import type { AnyAction } from 'redux';
import { createMigrate, persistReducer, REHYDRATE } from 'redux-persist';
import type { BaseReducer, PersistConfig } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { getIsUrlValidForErrorModal } from 'src/app/components/Error/Alert/utils';
import type { SIGNUP_SOURCES } from 'src/app/components/Pages/GetStarted/consts';
import {
  DEFAULT_ONBOARDING_STEPS,
  STEP_NAME as ONBOARDING_STEP_NAME,
} from 'src/app/components/Pages/GetStarted/StepLayout/consts';
import { calculateOnboardingSteps } from 'src/app/components/Pages/PromoOnboardingSteps/StepLayout/utils';
import type { OnboardingStep } from 'src/app/components/Pages/PromoOnboardingSteps/StepLayout/utils';
import { EMPTY_MEDICATION } from 'src/app/components/PageSections/DoctorTransferForm/validation';
import { update } from 'src/app/popsicle/utils';
import HISTORY from 'src/app/router/history';
import type { REDIRECT_POST_ONBOARDING } from 'src/app/state/actions/promoPageSignUp';
import * as actionTypes from 'src/app/state/actions/types';
import { getInitialLocationState, getInitialResourcesState } from 'src/app/state/initial';
import archivedMedicationsReducer from 'src/app/state/slices/archivedMedications';
import chatReducer from 'src/app/state/slices/chat';
import medicalHistoryFormReducer from 'src/app/state/slices/medicalHistoryForm';
import type { Account, CartItemForCheckout, Order, PatientDetails, PaymentCard } from 'src/app/types';
import { DeliveryMethodLabels, IS_PRODUCTION, REDUX_PERSIST_VERSION, SNACK_BAR_TYPES } from 'src/consts';

const DEFAULT_REDIRECT_REDUCER_STATE = {
  path: null,
};

function redirectReducer(state = DEFAULT_REDIRECT_REDUCER_STATE, action: AnyAction) {
  switch (action.type) {
    case actionTypes.AUTH_SET_REDIRECT:
      return update(state, {
        path: { $set: action.path },
      });
    case actionTypes.AUTH_CLEAR_REDIRECT:
      return update(state, {
        path: { $set: null },
      });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_REDIRECT_REDUCER_STATE;
    default:
      return state;
  }
}

/**
 * DANGER! HIGH VOLTAGE!
 *
 * The store's client points to the live client, which technically violates redux's immutability requirement
 */
function clientReducer(client: any = {}, action: AnyAction) {
  switch (action.type) {
    case actionTypes.CLIENT_REGISTER:
      return action.client;

    case actionTypes.CLIENT_START:
    case actionTypes.CLIENT_ACTION_FETCH_RESOURCE_FAILURE:
    default:
      return client;
  }
}

export function resourcesReducer(state: any = {}, action: AnyAction) {
  state = isEmpty(state) ? getInitialResourcesState() : state;

  switch (action.type) {
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return getInitialResourcesState();
    case actionTypes.CLEAR_RESOURCE:
      return update(state, { [action.resourceType.schemaName]: { $set: {} } });
    default:
      return state;
  }
}

const DEFAULT_ORDER_LOADING_STATE = { isLoading: false, fetched: false };

// JL: This is a hacky solution to get a loading state for the
// resourcesReducer. This should be deprecated as part of the
// deprecating Popsicle Epic https://capsulerx.atlassian.net/browse/RT-632
// @ts-ignore
const orderLoadingReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_ORDER_LOADING_STATE;

  switch (action.type) {
    case actionTypes.FETCH_ORDERS:
      draft.isLoading = true;
      break;
    case actionTypes.FETCH_ORDERS_SUCCESS:
      draft.isLoading = false;
      draft.fetched = true;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ORDER_LOADING_STATE;
  }
});

export interface BffOrderHistoryState {
  isFetching: boolean;
  hasFetched: boolean;
  data: { active: Order[]; past: Order[] } | null;
  error: string | null;
}

const DEFAULT_ORDER_HISTORY: BffOrderHistoryState = {
  isFetching: false,
  data: null,
  error: null,
  hasFetched: false,
};

export const bffOrderHistoryReducer = produce((draft: BffOrderHistoryState, action: AnyAction) => {
  if (!draft) return DEFAULT_ORDER_HISTORY;

  switch (action.type) {
    case actionTypes.FETCH_ORDER_HISTORY:
      draft.isFetching = true;
      draft.hasFetched = false;
      break;
    case actionTypes.FETCH_ORDER_HISTORY_SUCCESS:
      draft.isFetching = false;
      draft.data = action.data.data;
      draft.hasFetched = true;
      break;
    case actionTypes.FETCH_ORDER_HISTORY_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      draft.hasFetched = true;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ORDER_HISTORY;
  }
});

export interface BffOrderState {
  isFetching: boolean;
  data: { [orderId: string]: Order };
  error: string | null;
  numberOfOrders: number | null;
  hasTransfers: boolean | null;
}

const DEFAULT_ORDER: BffOrderState = {
  isFetching: false,
  data: {},
  error: null,
  numberOfOrders: null,
  hasTransfers: false,
};

export const bffOrderReducer = produce<BffOrderState>((draft, action) => {
  if (!draft) return DEFAULT_ORDER;

  // @ts-expect-error - immer doesn't seem to have `action` ever having `type` as a key.
  switch (action.type) {
    case actionTypes.CHECKOUT_CART_SUCCESS:
    case actionTypes.SET_NUMBER_OF_ORDERS:
      draft.numberOfOrders = action.numberOfOrders;
      break;
    case actionTypes.SET_HAS_TRANSFERS_FROM_GET_ENABLED_FEATURES_MAP_CALL:
      draft.hasTransfers = action.hasTransfers;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ORDER;
  }
});

const DEFAULT_MEDICATION_LOADING_STATES = {
  medications: {},
};

// @ts-ignore
const medicationLoadingStateReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_MEDICATION_LOADING_STATES;

  switch (action.type) {
    case actionTypes.SET_MEDICATION_LOADING_STATE:
      draft.medications[action.patientMedicationId] = action.isLoading;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_MEDICATION_LOADING_STATES;
  }
});

const DEFAULT_MEDICATION_DETAILS = {
  isFetching: false,
  data: {},
  currentPrescriptionId: null,
  error: null,
};

// @ts-ignore
const medicationDetailsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_MEDICATION_DETAILS;

  switch (action.type) {
    case actionTypes.GET_MEDICATION_DETAILS_REQUEST:
      draft.isFetching = true;
      break;
    case actionTypes.GET_MEDICATION_DETAILS_SUCCESS:
      draft.isFetching = false;
      draft.data[action.prescriptionId] = action.payload;
      break;
    case actionTypes.GET_MEDICATION_DETAILS_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.SET_CURRENT_PRESCRIPTION_ID:
      draft.currentPrescriptionId = action.prescriptionId;
      break;
    case actionTypes.CLEAR_MEDICATION_DETAILS_ERROR:
      draft.error = null;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_MEDICATION_DETAILS;
  }
});

const DEFAULT_MEDICATION_GUIDES = {
  isFetching: false,
  data: {},
  error: null,
};

// @ts-ignore
const medicationGuidesReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_MEDICATION_GUIDES;

  switch (action.type) {
    case actionTypes.GET_MEDICATION_GUIDES_REQUEST:
      draft.isFetching = true;
      draft.error = null;
      break;
    case actionTypes.GET_MEDICATION_GUIDES_SUCCESS:
      draft.isFetching = false;
      draft.data[action.ndc] = { html: action.payload };
      break;
    case actionTypes.GET_MEDICATION_GUIDES_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_MEDICATION_GUIDES;
  }
});

const DEFAULT_LOCATION_STATE = {
  preferredLocation: 'DEFAULT',
};

function locationReducer(state: any = {}, action: AnyAction) {
  state = isEmpty(state) ? getInitialLocationState() : state;

  switch (action.type) {
    case actionTypes.SET_LOCATION:
      return update(state, { preferredLocation: { $set: action.location } });
    case actionTypes.LOCATION_SHOW_DEBUG:
      return update(state, {
        showDebug: { $set: action.value },
      });
    case actionTypes.CLEAR_LOCATION:
      return update(state, { preferredLocation: { $set: DEFAULT_LOCATION_STATE.preferredLocation } });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_LOCATION_STATE;
    default:
      return state;
  }
}

const DEFAULT_TRANSFER_RX_STATE = {
  patientId: null,
};

function transferRxDependentReducer(state = DEFAULT_TRANSFER_RX_STATE, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SET_DEPENDENT:
      return update(state, {
        patientId: { $set: action.patientId },
      });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_TRANSFER_RX_STATE;
    default:
      return state;
  }
}

const DEFAULT_SESSION_STATE: {
  currentUserId: string | null;
  currentAccountId: string | null;
} = {
  currentUserId: null,
  currentAccountId: null,
};

export function sessionReducer(state = DEFAULT_SESSION_STATE, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SESSION_SET_CURRENT_USER_ID:
      return update(state, {
        currentUserId: { $set: action.currentUserId },
        currentAccountId: { $set: action.currentAccountId },
      });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return update(state, {
        currentUserId: { $set: null },
        currentAccountId: { $set: null },
      });
    default:
      return state;
  }
}

const DEFAULT_TRANSFER_MEDICATIONS = {};

function medicationsApiReducer(transfer = DEFAULT_TRANSFER_MEDICATIONS, action: AnyAction) {
  switch (action.type) {
    case actionTypes.RECEIVE_MEDICATIONS_BY_NAME:
      return update(transfer, {
        [action.medicationName]: { $set: action.items },
      });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_TRANSFER_MEDICATIONS;
    default:
      return transfer;
  }
}

interface MedicationsProps {
  medicationName: string;
  key: number;
  showControlledDrugError: boolean;
  ref: RefObject<unknown>;
}

interface MedicationFormsProps {
  medications: { [key: number]: MedicationsProps };
  count: number;
}

const DEFAULT_MEDICATION_FORMS: MedicationFormsProps = {
  medications: {
    0: {
      medicationName: 'medications_0',
      key: 0,
      showControlledDrugError: false,
      ref: createRef(),
    },
  },
  count: 0,
};

function medicationFormsReducer(state = DEFAULT_MEDICATION_FORMS, action: AnyAction) {
  switch (action.type) {
    case actionTypes.CREATE_MEDICATION_FORM:
      const incrementedCount = state.count + 1;
      const newMedicationForm = {
        medicationName: `medications_${incrementedCount}`,
        showControlledDrugError: false,
        key: incrementedCount,
        ref: createRef(),
      };
      return update(state, {
        count: { $set: incrementedCount },
        medications: {
          [incrementedCount]: { $set: newMedicationForm },
        },
      });
    case actionTypes.UPDATE_MEDICATION_SHOW_CONTROLLED_FORM:
      return update(state, {
        medications: {
          [action.medicationFormIndex]: { $set: action.medicationForm },
        },
      });
    case actionTypes.DELETE_MEDICATION_FORM:
      const newForm: { [key: string]: MedicationsProps } = {};
      for (const key in state.medications) {
        if (parseInt(key, 10) !== action.medicationFormIndex) {
          newForm[key] = state.medications[key];
        }
      }
      // Need to decrement the count if the medication field being deleted is the last one
      // Count is used to get the last medication form when we want to focus or delete it
      const currentCount = state.count === action.medicationFormIndex ? state.count - 1 : state.count;
      return update(state, {
        medications: {
          $set: newForm,
        },
        count: {
          $set: currentCount,
        },
      });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_MEDICATION_FORMS;
    default:
      return state;
  }
}

interface DoctorFormsProps {
  forms: { [key: number]: Record<string, unknown> };
}

const DEFAULT_DOCTOR_FORMS: DoctorFormsProps = {
  forms: {},
};

function doctorFormsReducer(state = DEFAULT_DOCTOR_FORMS, action: AnyAction) {
  switch (action.type) {
    case actionTypes.CREATE_DOCTOR_FORM:
      return update(state, {
        forms: {
          [action.doctorFormIndex]: { $set: action.doctorForm },
        },
      });
    case actionTypes.UPDATE_DOCTOR_FORM:
      return update(state, {
        forms: {
          [action.doctorFormIndex]: { $set: action.doctorForm },
        },
      });

    case actionTypes.DELETE_ALL_DOCTOR_FORMS:
      return update(state, {
        forms: {
          $set: {},
        },
      });
    case actionTypes.DELETE_DOCTOR_FORM:
      const newForm: { [key: number]: Record<string, unknown> } = {};
      for (const key in state.forms) {
        if (parseInt(key, 10) !== action.doctorFormIndex) {
          newForm[key] = state.forms[key];
        }
      }
      return update(state, {
        forms: {
          $set: newForm,
        },
      });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_DOCTOR_FORMS;
    default:
      return state;
  }
}

function setEditAddressStateReducer(state: any = {}, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SET_EDIT_ADDRESS_STATE:
      const { id, notes, address, addressTwo, zipCode, isDoorman } = action.address;
      // edit address uses pharmakon endpoint with steet, street_two, etc in snakecase.
      // account page addresses come from BFF endpoint that uses address, addressTwo, etc in camel case.
      return update(state, {
        id: { $set: id },
        street: { $set: address ? address : '' },
        street_two: { $set: addressTwo ? addressTwo : '' },
        zip_code: { $set: zipCode ? zipCode : '' },
        notes: { $set: notes ? notes : '' },
        is_doorman: { $set: isDoorman },
      });
    case actionTypes.CLEAR_EDIT_ADDRESS_STATE:
      return {};
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return {};
    default:
      return state;
  }
}

const DEFAULT_CHECKOUT = {
  cart: [], // Array<Object{ fulfillmentId, productId, price }>
  orderId: null, // <Order#id>?
  cartId: null,
  requiresAccount: false,
  paymentCardId: null,
  DeliveryAddress: null, // Object{ DeliveryAddress#id: { attributes } }
  DeliveryWindow: null, // Object{ DeliveryWindow#id: { type, id, attributes } }
  selectedDeliveryMethodLabel: DeliveryMethodLabels.freeDelivery,
};

// @ts-ignore
export const checkoutReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_CHECKOUT;

  switch (action.type) {
    case actionTypes.CART_ADD_ITEMS:
      draft.cart = uniq([...draft.cart, ...action.items]);
      draft.orderId = null;
      draft.cartId = null;
      draft.paymentCardId = null;
      draft.DeliveryAddress = null;
      draft.DeliveryWindow = null;
      break;
    // updating items from a cart such as otc item quantity
    case actionTypes.CART_UPDATE_ITEMS:
      draft.cart.forEach((item: CartItemForCheckout) => {
        if (item.otcItemId === action.otcItemId) {
          item.quantity = action.quantity;
          item.price = action.price ?? item.price;
        }
      });
      draft.orderId = null;
      draft.cartId = null;
      draft.paymentCardId = null;
      draft.DeliveryAddress = null;
      draft.DeliveryWindow = null;
      break;
    case actionTypes.CART_REMOVE_ITEMS:
      draft.cart = draft.cart.filter(
        (item: CartItemForCheckout) =>
          item.fulfillmentId !== action.items[0].fulfillmentId || item.otcItemId !== action.items[0].otcItemId
      );
      draft.orderId = null;
      draft.cartId = action.cartId ?? null;
      draft.paymentCardId = null;
      draft.DeliveryAddress = null;
      draft.DeliveryWindow = null;
      break;
    case actionTypes.CHECKOUT_SET_ORDER:
      draft.orderId = action.orderId;
      break;
    case actionTypes.CHECKOUT_REQUIRES_ACCOUNT:
      draft.requiresAccount = true;
      break;
    // Selecting a DeliveryAddress should clear the previously selected Delivery Window
    // Since DeliveryWindows are dependent on what Address is selected.
    case actionTypes.CHECKOUT_SET_DELIVERY_ADDRESS:
      draft.DeliveryAddress = action.deliveryAddress;
      draft.DeliveryWindow = null;
      break;
    case actionTypes.CHECKOUT_SET_PAYMENT_CARD_ID:
      draft.paymentCardId = action.paymentCardId;
      break;
    case actionTypes.CHECKOUT_SET_DELIVERY_WINDOW:
      draft.DeliveryWindow = action.deliveryWindow;
      break;
    case actionTypes.CHECKOUT_SET_CART_ID:
      draft.cartId = action.cartId;
      break;

    case actionTypes.CART_CLEAR:
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_CHECKOUT;
    case actionTypes.SET_SELECTED_DELIVERY_METHOD_LABEL_FROM_CHECKOUT:
      draft.selectedDeliveryMethodLabel = action.selectedDeliveryMethodLabel;
  }
});

export interface OnboardingMedication {
  medicationName: string;
  hicql_seqno: string;
}

export interface BffOnboardingAccountInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  referral: string;
  self_attribution: string;
  agreed_to_terms_version: string;
  signup_source: string;
}

export interface BffOnboardingRxTransferInfo {
  pharmacyDetails: string;
  pharmacyName: string;
  medications: OnboardingMedication[];
}
export interface BffOnboardingState {
  prescriptionsInfo: BffOnboardingRxTransferInfo;
  accountInfo: BffOnboardingAccountInfo;
}

const DEFAULT_HORIZONTAL_PROGRESS_BAR_STATE = { isShown: false };

function horizontalProgressBarReducer(state = DEFAULT_HORIZONTAL_PROGRESS_BAR_STATE, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SET_HORIZONTAL_PROGRESS_BAR_VISIBILITY:
      return update(state, {
        isShown: { $set: action.prescriptionsInfo },
      });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_HORIZONTAL_PROGRESS_BAR_STATE;
    default:
      return state;
  }
}

const DEFAULT_LANDING_PAGE_MODAL_STATE = { open: false };

function landingPageModalReducer(state = DEFAULT_LANDING_PAGE_MODAL_STATE, action: AnyAction) {
  if (action.type === actionTypes.LANDING_MODAL) {
    return { ...state, open: action.modalState };
  } else if (action.type === actionTypes.SESSION_CLEAR_CURRENT_USER_ID) {
    return DEFAULT_LANDING_PAGE_MODAL_STATE;
  }

  return state;
}

export interface MedicationManagementReducer {
  data: any;
  pendingTransfers: any[];
  optimisticPendingTransfers: any[];
  error: Error | null;
  isFetching: boolean;
  hasFetched: boolean;
  isOpen: boolean;
  hasPrimaryEverSubmittedTransfer: boolean;
}

const DEFAULT_MEDICATION_MANAGEMENT: MedicationManagementReducer = {
  data: null,
  pendingTransfers: [],
  optimisticPendingTransfers: [],
  error: null,
  isFetching: false,
  hasFetched: false,
  isOpen: false,
  hasPrimaryEverSubmittedTransfer: false,
};

export const medicationManagementReducer = produce<MedicationManagementReducer>((draft, action) => {
  if (!draft) return DEFAULT_MEDICATION_MANAGEMENT;

  // @ts-ignore
  switch (action.type) {
    case actionTypes.FETCH_MEDS_REQUEST:
      draft.hasFetched = false;
      draft.isFetching = true;
      break;
    case actionTypes.FETCH_MEDS_SUCCESS:
      draft.isFetching = false;
      draft.data = action.data;
      draft.pendingTransfers = action.pendingTransfers;
      draft.hasPrimaryEverSubmittedTransfer = action.hasPrimaryEverSubmittedTransfer;
      draft.hasFetched = true;
      break;
    case actionTypes.FETCH_PENDING_TRANSFERS_SUCCESS:
      draft.isFetching = false;
      draft.pendingTransfers = action.pendingTransfers;
      break;
    case actionTypes.SET_OPTIMISTIC_PENDING_TRANSFER:
      draft.optimisticPendingTransfers = action.optimisticPendingTransfers;
      break;
    case actionTypes.FETCH_MEDS_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      draft.hasFetched = true;
      break;
    case actionTypes.TOGGLE_HOME_SCREEN_ARCHIVE_SECTION:
      draft.isOpen = action.isOpen;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_MEDICATION_MANAGEMENT;
  }
});

const DEFAULT_MODAL_STATE = {
  modalType: null,
};

function modalReducer(state = DEFAULT_MODAL_STATE, action: AnyAction) {
  if (action.type === actionTypes.MODAL) {
    return { ...state, ...action.modal };
  } else if (action.type === actionTypes.SESSION_CLEAR_CURRENT_USER_ID) {
    return DEFAULT_MODAL_STATE;
  }

  return state;
}

const DEFAULT_ADDRESS_VALIDATION = {
  userValidatedAddress: false, // boolean, set to true if a user edits an already input address
  invalidAddress: null, // object
  suggestedAddress: null, // array of objects
  successAction: null,
  pushEditRoute: null,
};

function addressValidationReducer(state = DEFAULT_ADDRESS_VALIDATION, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SET_VALIDATE_ADDRESS_SUGGESTIONS:
      return update(state, {
        invalidAddress: { $set: action.data.invalidAddress },
        suggestedAddress: { $set: action.data.suggestedAddress },
        successAction: { $set: action.data.successAction },
        pushEditRoute: { $set: action.data.pushEditRoute },
      });
    case actionTypes.SET_VALIDATE_ADDRESS_FLAG:
      return update(state, {
        userValidatedAddress: { $set: action.data.userValidatedAddress },
      });
    case actionTypes.CLEAR_ADDRESS_VALIDATION:
      return DEFAULT_ADDRESS_VALIDATION;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ADDRESS_VALIDATION;
    default:
      return state;
  }
}

const DEFAULT_INSURANCE_STATE = {
  isFetching: false,
  list: null,
  pendingStatus: null,
  uploadInsurance: {
    isUploading: false,
    isUploaded: false,
    error: null,
  },
  hasFetched: false,
};

// @ts-ignore
const insuranceReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_INSURANCE_STATE;
  switch (action.type) {
    case actionTypes.FETCHING_INSURANCE:
      draft.isFetching = true;
      draft.hasFetched = false;
      break;
    case actionTypes.FETCHED_INSURANCE_SUCCESS:
      draft.list = action.data;
      draft.hasFetched = true;
      draft.isFetching = false;
      break;
    case actionTypes.FETCHED_PENDING_INSURANCE_SUCCESS:
      if (draft.pendingStatus?.submittedOn != null && action.attributes?.submittedOn == null) {
        action.attributes.submittedOn = draft.pendingStatus?.submittedOn;
      }
      draft.pendingStatus = action.attributes;
      break;
    case actionTypes.UPLOAD_INSURANCE_REQUEST:
      draft.uploadInsurance.isUploading = true;
      break;
    case actionTypes.UPLOAD_INSURANCE_SUCCESS:
      draft.uploadInsurance.isUploading = false;
      draft.uploadInsurance.isUploaded = true;
      break;
    case actionTypes.UPLOAD_INSURANCE_FAILURE:
      draft.uploadInsurance.isUploading = false;
      draft.uploadInsurance.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_INSURANCE_STATE;
  }
});

interface PromoSignUpState {
  zipCode: number | null;
  defaultZipCode: number | null;
  transferMedications: any[];
  hasExistingAccount: boolean;
  promoCode: string | null;
  workEmail: string | null;
  currentOnboardingStepIndex: number;
  onboardingSteps: OnboardingStep[] | null;
  postOnboardingRedirect: keyof typeof REDIRECT_POST_ONBOARDING;
  isCapsuleAtWork: boolean;
  employeeDescription: string | null;
}
const DEFAULT_PROMO_SIGN_UP: PromoSignUpState = {
  zipCode: null,
  defaultZipCode: null,
  transferMedications: [],
  hasExistingAccount: false,
  promoCode: null,
  workEmail: null,
  currentOnboardingStepIndex: 0,
  onboardingSteps: calculateOnboardingSteps({
    steps: ['ADD_A_PRESCRIPTION'],
    hidden: [],
  }),
  postOnboardingRedirect: 'shop',
  isCapsuleAtWork: false,
  employeeDescription: null,
};

// @ts-ignore
const promoOnboardingReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_PROMO_SIGN_UP;

  switch (action.type) {
    case actionTypes.SET_PROMO_ONBOARDING_HAS_EXISTING_ACCOUNT:
      draft.hasExistingAccount = action.hasExistingAccount;
      break;
    case actionTypes.SET_PROMO_ONBOARDING_WORK_EMAIL:
      draft.workEmail = action.workEmail;
      break;
    case actionTypes.SET_PROMO_CODE_FOR_ONBOARDING:
      draft.promoCode = action.promoCode;
      break;
    case actionTypes.SET_ZIP_CODE_PROMO_SIGN_UP:
      draft.zipCode = action.zipCode;
      break;
    case actionTypes.SET_NAVIGATE_TO_NEXT_PROMO_ONBOARDING_STEP:
      draft.currentOnboardingStepIndex = action.newStepIndex;
      break;
    case actionTypes.SET_CURRENT_PROMO_ONBOARDING_STEP:
      draft.currentOnboardingStepIndex = action.currentOnboardingStepIndex;
      break;
    case actionTypes.SET_PROMO_TRANSFER_MEDICATIONS:
      draft.transferMedications = action.transferMedications;
      break;
    case actionTypes.FETCH_PROMO_CODE_DETAILS_SUCCESS:
      draft.onboardingSteps = calculateOnboardingSteps(action.data.onboardingSteps);
      draft.currentOnboardingStepIndex = 0;
      draft.postOnboardingRedirect = action.data.postOnboardingRedirect;
      draft.isCapsuleAtWork = action.data.isCapsuleAtWork;
      draft.defaultZipCode = action.data.defaultZipCode;
      draft.promoCode = action.data.promoCode;
      draft.employeeDescription = action.data.employeeDescription;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_PROMO_SIGN_UP;
  }
});

const DEFAULT_BFF_INSURANCE_STATE = {
  isFetching: false,
  data: null,
  error: null,
};

// @ts-ignore
const bffInsuranceReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_BFF_INSURANCE_STATE;

  switch (action.type) {
    case actionTypes.FETCH_LEGACY_INSURANCE_REQUEST:
      draft.isFetching = true;
      break;
    case actionTypes.FETCH_LEGACY_INSURANCE_SUCCESS:
      draft.isFetching = false;
      draft.data = action.data;
      break;
    case actionTypes.FETCH_LEGACY_INSURANCE_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_BFF_INSURANCE_STATE;
  }
});

const DEFAULT_VERIFY_EMAIL_STATE = {
  isFetching: false,
  isFetched: false,
  isVerified: null,
  error: null,
};

// @ts-ignore
const verifyEmailReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_VERIFY_EMAIL_STATE;

  switch (action.type) {
    case actionTypes.FETCH_VERIFY_EMAIL_REQUEST:
      draft.isFetching = true;
      draft.isFetched = false;
      break;
    case actionTypes.FETCH_VERIFY_EMAIL_SUCCESS:
      draft.isFetching = false;
      draft.isFetched = true;
      draft.isVerified = action.data;
      break;
    case actionTypes.FETCH_VERIFY_EMAIL_FAILURE:
      draft.isFetching = false;
      draft.isFetched = true;
      draft.error = action.error;
      break;
    case actionTypes.CLEAR_VERIFY_EMAIL:
      return DEFAULT_VERIFY_EMAIL_STATE;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_VERIFY_EMAIL_STATE;
  }
});

const DEFAULT_BFF_OTC_ITEMS_STATE = {
  isFetching: false,
  data: null,
  error: null,
};

// @ts-ignore
const bffOTCItemsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_BFF_OTC_ITEMS_STATE;

  switch (action.type) {
    case actionTypes.FETCH_OTC_ITEMS_REQUEST:
      draft.isFetching = true;
      break;
    case actionTypes.FETCH_OTC_ITEMS_SUCCESS:
      draft.isFetching = false;
      draft.data = action.data;
      break;
    case actionTypes.FETCH_OTC_ITEMS_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_BFF_OTC_ITEMS_STATE;
  }
});

const DEFAULT_BFF_CART_DELIVERY_METHODS_STATE = {
  isFetching: false,
  data: null,
  error: null,
};

// @ts-ignore
const bffCartDeliveryMethodsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_BFF_CART_DELIVERY_METHODS_STATE;

  switch (action.type) {
    case actionTypes.FETCH_CART_DELIVERY_METHODS_REQUEST:
      draft.isFetching = true;
      break;
    case actionTypes.FETCH_CART_DELIVERY_METHODS_SUCCESS:
      draft.isFetching = false;
      draft.data = action.data;
      break;
    case actionTypes.FETCH_CART_DELIVERY_METHODS_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_BFF_CART_DELIVERY_METHODS_STATE;
  }
});

const DEFAULT_BFF_CART_SHIPPING_OPTIONS_STATE = {
  isFetching: false,
  data: null,
  error: null,
};

// @ts-ignore
const bffCartShippingOptionsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_BFF_CART_SHIPPING_OPTIONS_STATE;

  switch (action.type) {
    case actionTypes.FETCH_CART_DELIVERY_SHIPPING_OPTIONS_REQUEST:
      draft.isFetching = true;
      break;
    case actionTypes.FETCH_CART_DELIVERY_SHIPPING_OPTIONS_SUCCESS:
      draft.isFetching = false;
      draft.data = action.data;
      break;
    case actionTypes.FETCH_CART_DELIVERY_SHIPPING_OPTIONS_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_BFF_CART_SHIPPING_OPTIONS_STATE;
  }
});

const DEFAULT_BFF_CART_STATE = {
  isFetching: false,
  data: null,
  error: null,
};

// @ts-ignore
export const bffCartReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_BFF_CART_STATE;

  switch (action.type) {
    case actionTypes.FETCH_CART_REQUEST:
      draft.isFetching = true;
      break;
    case actionTypes.FETCH_CART_SUCCESS:
      draft.isFetching = false;
      draft.data = action.data;
      break;
    case actionTypes.FETCH_CART_FAILURE:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.UPDATE_BFF_CART:
      draft.data = action.data;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_BFF_CART_STATE;
  }
});

const DEFAULT_CHECKOUT_TRANSFER_MODAL_STATE = {
  show: null,
};

// @ts-ignore
const checkoutTransferModalReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_CHECKOUT_TRANSFER_MODAL_STATE;

  switch (action.type) {
    case actionTypes.SET_SHOW_CHECKOUT_TRANSFER_MODAL:
      draft.show = action.show;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_CHECKOUT_TRANSFER_MODAL_STATE;
  }
});

const DEFAULT_PAGE_ALERT_STATE = {
  type: null,
};

function pageAlertReducer(state = DEFAULT_PAGE_ALERT_STATE, action: AnyAction) {
  if (action.type === actionTypes.PAGE_ALERT) {
    return { ...state, type: action.pageAlertType };
  } else if (action.type === actionTypes.SESSION_CLEAR_CURRENT_USER_ID) {
    return DEFAULT_PAGE_ALERT_STATE;
  }

  return state;
}

const DEFAULT_SNACK_BAR_STATE = {
  [SNACK_BAR_TYPES.tier_message]: true,
};

function SnackBarReducer(state = DEFAULT_SNACK_BAR_STATE, action: AnyAction) {
  if (action.type === actionTypes.SNACK_BAR) {
    return { ...state, [action.snackBarType]: action.snackBarState };
  } else if (action.type === actionTypes.SESSION_CLEAR_CURRENT_USER_ID) {
    return DEFAULT_SNACK_BAR_STATE;
  }

  return state;
}

const DEFAULT_NAVBAR_STATE = {
  hideNavLinks: false,
  hideNavBar: false,
};

const DEFAULT_USER_PREFERENCES_STATE = {
  hasSeenOTCTransferMedsModal: false,
  dismissedNextBestActionCards: [],
  newArchivedPageModalShown: false,
};

export type UserPreferenceReducer = typeof DEFAULT_USER_PREFERENCES_STATE;

const userPreferencesReducer = produce<UserPreferenceReducer>((draft, action) => {
  if (!draft) return DEFAULT_USER_PREFERENCES_STATE;

  // @ts-ignore - type is definitely a property on `action`
  switch (action.type) {
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      draft.hasSeenOTCTransferMedsModal = false;
      break;
    case actionTypes.CLOSE_NEXT_BEST_ACTION_CARD:
      // @ts-ignore - 🤷‍♂️
      draft.dismissedNextBestActionCards.push(action.key);
      break;
    case actionTypes.RESET_CLOSED_NEXT_BEST_ACTION_CARDS:
      draft.dismissedNextBestActionCards = [];
      break;
    case actionTypes.SET_NEW_ARCHIVED_PAGE_MODAL_SHOWN:
      draft.newArchivedPageModalShown = true;
      break;
    case actionTypes.RESET_NEW_ARCHIVED_PAGE_MODAL_SHOWN:
      draft.newArchivedPageModalShown = false;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_USER_PREFERENCES_STATE;
  }
});

const DEFAULT_UI_STATE = {
  loading: {},
  navBar: DEFAULT_NAVBAR_STATE,
  userPreferences: DEFAULT_USER_PREFERENCES_STATE,
};

export function uiReducer(state = DEFAULT_UI_STATE, action: AnyAction) {
  return combineReducers({
    navBar: uiNavBarReducer,
    userPreferences: userPreferencesReducer,
  })(state, action);
}

const DEFAULT_NETWORK_ERROR_STATE = {
  genericApi: false,
  networkConnectivityError: false,
  customApi: null,
};

export interface CustomErrorModal {
  title: string;
  flavorText: string;
  errorCode: string;
  silent: boolean;
  canDismiss: boolean;
}

// @ts-ignore
const networkErrorsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_NETWORK_ERROR_STATE;
  switch (action.type) {
    case actionTypes.SET_CUSTOM_ERROR_MODAL:
      const { title, flavorText, errorCode, silent = false, canDismiss = true } = action.error;
      draft.customApi = { title, flavorText, errorCode, silent, canDismiss };
      break;
    case actionTypes.CLEAR_NETWORK_ERRORS:
      draft.customApi = null;
      draft.genericApi = false;
      draft.networkConnectivityError = false;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_NETWORK_ERROR_STATE;
    default:
      const { error } = action;
      if (error) {
        const status = error.response?.status ?? NaN;
        const url = error?.response?.config?.url;
        if (getIsUrlValidForErrorModal(url)) {
          const { title, flavorText, errorCode, silent = false, canDismiss = true } = error.response?.data ?? {};
          if (errorCode && flavorText) {
            draft.customApi = { title, flavorText, errorCode, silent, canDismiss };
          } else if (status === 504) {
            draft.networkConnectivityError = true;
          } else if ((status >= 500 && status < 600) || status === 404) {
            draft.genericApi = true;
          }
        }
      }
      break;
  }
});

function uiNavBarReducer(state = DEFAULT_NAVBAR_STATE, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SET_HIDE_NAV_LINKS:
    case actionTypes.SET_MEDICATION_SUCCESS:
      return {
        ...state,
        hideNavLinks: true,
      };
    case actionTypes.TOGGLE_NAV_HEADER:
      return {
        ...state,
        hideNavBar: action.hideNavBar,
      };
    case actionTypes.RESET_NAV_BAR:
      return DEFAULT_NAVBAR_STATE;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_NAVBAR_STATE;
    default:
      return state;
  }
}

const DEFAULT_DISPLAY_MODAL_STATE = {
  family: {
    showModal: false,
  },
  faq: {
    showModal: false,
  },
  medicationDirections: {
    showModal: false,
    homeItem: null,
  },
  otc: {
    showModal: false,
  },
  newArchivedPage: {
    showModal: false,
  },
};

// @ts-ignore
const displayModalReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_DISPLAY_MODAL_STATE;

  switch (action.type) {
    case actionTypes.FAMILY_MODAL:
      draft.family.showModal = action.showModal;
      draft.family.dependentName = action.dependentName;
      break;
    case actionTypes.FAQ_MODAL:
      draft.faq.showModal = action.showModal;
      break;
    case actionTypes.OTC_MODAL:
      draft.otc = { showModal: action.showModal };
      break;
    case actionTypes.NEW_ARCHIVED_PAGE_MODAL:
      draft.newArchivedPage = { showModal: action.showModal };
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_DISPLAY_MODAL_STATE;
  }
});

function removeBadPersistedCheckoutData(state: any = {}) {
  let updatedState = state;

  // clean up cart items which are problematic
  const allCartItems = state?.checkout?.cart ?? [];
  if (allCartItems.some((item: CartItemForCheckout) => isNil(item.fulfillmentId) && isNil(item.otcItemId))) {
    updatedState = update(updatedState, { $unset: ['checkout'] });
  }

  return updatedState;
}

const DEFAULT_ROUTER_LOCATION_STATE: {
  previous: Location | null;
  next: Location | null;
} = {
  previous: null,
  next: null,
};

type RouterLocationState = typeof DEFAULT_ROUTER_LOCATION_STATE;

const routerLocationReducer = produce<RouterLocationState>((draft, action) => {
  if (!draft) return DEFAULT_ROUTER_LOCATION_STATE;

  // fighting typescript here
  const _action = (action ?? {}) as unknown as {
    type: string;
    currentRouterLocation: Location;
    payload: {
      action: Action;
      location: Location;
      isFirstRendering: boolean;
    };
  };

  const { type, currentRouterLocation, payload } = _action;

  switch (type) {
    case LOCATION_CHANGE:
      draft.previous = draft.next;
      draft.next = payload.location;
      break;
    case actionTypes.SET_CURRENT_ROUTER_LOCATION:
      draft.next = currentRouterLocation;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ROUTER_LOCATION_STATE;
  }
});

const DEFAULT_FACILITY = {
  isFetching: false,
  id: null,
};
export function facilityReducer(state = DEFAULT_FACILITY, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SET_FACILITY_IS_FETCHING:
      return {
        ...state,
        isFetching: true,
      };
    case actionTypes.SET_FACILITY_SUCCESS:
      return {
        ...state,
        ...action.facility,
        isFetching: false,
      };
    case actionTypes.SET_FACILITY_ERROR:
      return {
        ...state,
        isFetching: false,
      };
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_FACILITY;
    default:
      return state;
  }
}

export interface AccountReducerShape {
  isFetching: boolean;
  hasFetched: boolean;
  data: (Account & { stripeDefaultCardId?: string | null; signupSource?: SIGNUP_SOURCES }) | null;
  error: Error | string | null;
  passwordResetRequired?: boolean;
}

const DEFAULT_ACCOUNT: AccountReducerShape = {
  isFetching: false,
  hasFetched: false,
  data: null,
  error: null,
  passwordResetRequired: false,
};

const accountReducer = produce<AccountReducerShape>((draft, untypedAction) => {
  if (!draft) return DEFAULT_ACCOUNT;

  const action = untypedAction as unknown as AnyAction & AccountReducerShape;

  switch (action.type) {
    case actionTypes.SET_ACCOUNT_IS_FETCHING:
      draft.isFetching = true;
      draft.hasFetched = false;
      break;
    case actionTypes.SET_ACCOUNT_SUCCESS:
      draft.isFetching = false;
      draft.data = action.account;
      draft.hasFetched = true;
      break;
    case actionTypes.SET_ACCOUNT_ERROR:
      draft.isFetching = false;
      draft.error = action.error;
      draft.hasFetched = true;
      break;
    case actionTypes.SET_PAYMENT_CARDS_DELETE_SUCCESS:
      // @ts-ignore
      draft.data = {
        ...draft.data,
        stripeDefaultCardId: null,
      };
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ACCOUNT;
  }
});

const DEFAULT_ACCOUNT_UPDATE_STATE = { isLoading: false, needsRefresh: false };
/**
 * NOTE (JL): This is a kind of hacky solution to handle the fact that we're STILL fetching
 * account data from Pharmakon instead of the BFF. The response from the Pharmakon update
 * account endpoint is not a full account object, but just two fields,
 * signup_source & sent_from_phone_number. Once we transition to ONLY fetch account data from
 * the BFF this should be combined with the above accountReducer.
 *
 */
// @ts-ignore
const accountUpdateReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_ACCOUNT_UPDATE_STATE;

  switch (action.type) {
    case actionTypes.SET_ACCOUNT_UPDATE_REQUEST:
      draft.isLoading = true;
      break;
    case actionTypes.SET_ACCOUNT_UPDATE_SUCCESS:
      draft.isLoading = false;
      draft.needsRefresh = true;
      break;
    case actionTypes.SET_ACCOUNT_UPDATE_ERROR:
      draft.isLoading = false;
      break;
    case actionTypes.SET_PHARMAKON_ACCOUNT_UPDATED:
    case actionTypes.SET_ACCOUNT_SUCCESS:
      draft.needsRefresh = false;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ACCOUNT_UPDATE_STATE;
  }
});

interface PatientReducer {
  isFetching: boolean;
  data: PatientDetails[] | null;
  error: Error | null;
}

const DEFAULT_PATIENT: PatientReducer = {
  isFetching: false,
  data: null,
  error: null,
};

export const patientReducer = produce<PatientReducer>((draft, untypedAction) => {
  if (!draft) return DEFAULT_PATIENT;
  const action = untypedAction as unknown as {
    type: string;
    patient: PatientDetails[];
    error: Error;
  };

  switch (action.type) {
    case actionTypes.SET_PATIENT_IS_FETCHING:
      draft.isFetching = true;
      break;
    case actionTypes.SET_PATIENT_SUCCESS:
      draft.isFetching = false;
      draft.data = action.patient;
      break;
    case actionTypes.SET_PATIENT_ERROR:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_PATIENT;
  }
});

const DEFAULT_PAYMENT_CARDS = {
  isFetching: false,
  data: null,
  error: null,
};

// @ts-ignore
const paymentCardsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_PAYMENT_CARDS;

  switch (action.type) {
    case actionTypes.SET_PAYMENT_CARDS_IS_FETCHING:
      draft.isFetching = true;
      draft.error = null;
      break;
    case actionTypes.SET_PAYMENT_CARDS_SUCCESS:
      draft.data = action.paymentCards;
      draft.isFetching = false;
      break;
    case actionTypes.SET_PAYMENT_CARDS_ERROR:
      draft.error = action.error;
      draft.isFetching = false;
      break;
    case actionTypes.SET_PAYMENT_CARDS_UPDATE_SUCCESS:
      draft.data = [...draft.data, action.paymentCard];
      break;
    case actionTypes.SET_PAYMENT_CARDS_UPDATE_ERROR:
      draft.error = action.error;
      break;
    case actionTypes.SET_PAYMENT_CARDS_DELETE_SUCCESS:
      draft.data = draft.data.filter((card: PaymentCard) => card.id !== action.cardId);
      break;
    case actionTypes.SET_PAYMENT_CARDS_DELETE_ERROR:
      draft.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_PAYMENT_CARDS;
  }
});

const DEFAULT_PICKUP_STATIONS = {
  isFetching: false,
  data: null,
  error: null,
};

// @ts-ignore
const checkoutPickupStationsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_PICKUP_STATIONS;

  switch (action.type) {
    case actionTypes.FETCH_CHECKOUT_PICKUP_STATIONS:
      draft.isFetching = true;
      draft.error = null;
      break;
    case actionTypes.FETCH_CHECKOUT_PICKUP_STATIONS_SUCCESS:
      draft.isFetching = false;
      draft.data = action.payload;
      break;
    case actionTypes.FETCH_CHECKOUT_PICKUP_STATIONS_ERROR:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.CLEAR_CHECKOUT_PICKUP_STATIONS:
      draft.data = null;
      draft.error = null;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_PICKUP_STATIONS;
  }
});

const DEFAULT_DELIVERYADDRESS = {
  isFetching: false,
  data: null,
  error: null,
};

// @ts-ignore
const deliveryAddressReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_DELIVERYADDRESS;

  switch (action.type) {
    case actionTypes.SET_DELIVERYADDRESS_IS_FETCHING:
      draft.isFetching = true;
      break;
    case actionTypes.SET_DELIVERYADDRESS_SUCCESS:
      draft.isFetching = false;
      draft.data = action.deliveryAddress;
      break;
    case actionTypes.SET_DELIVERYADDRESS_ERROR:
      draft.isFetching = false;
      draft.error = action.error;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_DELIVERYADDRESS;
  }
});

const DEFAULT_CHECKOUT_DELIVERY_WINDOWS_STATE = {
  isFetching: false,
  error: null,
  data: null,
};

// @ts-ignore
const checkoutDeliveryWindowsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_CHECKOUT_DELIVERY_WINDOWS_STATE;

  switch (action.type) {
    case actionTypes.REQUEST_CHECKOUT_DELIVERY_WINDOWS:
      draft.isFetching = true;
      break;
    case actionTypes.FETCH_CHECKOUT_DELIVERY_WINDOWS_SUCCESS:
      draft.isFetching = false;
      draft.data = action.data;
      draft.error = null;
      break;
    case actionTypes.FETCH_CHECKOUT_DELIVERY_WINDOWS_ERROR:
      draft.isFetching = false;
      draft.error = action.error;
      draft.data = null;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_CHECKOUT_DELIVERY_WINDOWS_STATE;
  }
});

const DEFAULT_ORDER_STATUS_STATE = {};

// @ts-ignore
const orderStatusReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_ORDER_STATUS_STATE;

  switch (action.type) {
    case actionTypes.SET_ORDER_STATUS:
      if (draft[action.orderId]) {
        draft[action.orderId].status = action.orderStatus;
      } else {
        draft[action.orderId] = { status: action.orderStatus };
      }
      break;

    case actionTypes.SET_ORDER_STATUS_LOCATION:
      if (draft[action.orderId]) {
        draft[action.orderId].locations = action.orderLocations;
      } else {
        draft[action.orderId] = { locations: action.orderLocations };
      }
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ORDER_STATUS_STATE;
  }
});

const DEFAULT_REQUEST_REFILL_MODAL_STATUS = {
  isShown: false,
  success: undefined,
};

function requestRefillModalStatusReducer(state = DEFAULT_REQUEST_REFILL_MODAL_STATUS, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SET_REQUEST_REFILL_MODAL_STATUS:
      return update(state, {
        isShown: { $set: action.isShown },
        success: { $set: action.success },
      });
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_REQUEST_REFILL_MODAL_STATUS;
    default:
      return state;
  }
}

const DEFAULT_TRANSFERS = {
  medications: null,
  escriptCounts: null,
};
function transfersReducer(state = DEFAULT_TRANSFERS, action: AnyAction) {
  switch (action.type) {
    case actionTypes.SET_MEDICATION_SUCCESS:
      return { ...state, medications: action.medications };
    case actionTypes.SET_ONBOARDING_ESCRIPTS:
      return { ...state, escriptCounts: action.escriptCounts };
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_TRANSFERS;
    default:
      return state;
  }
}

const onboardingStepsReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_ONBOARDING_STEPS;
  switch (action.type) {
    case actionTypes.ADD_ONBOARDING_STEP:
      draft.splice(action.index, 0, action.step);
      return draft;
    case actionTypes.REMOVE_ONBOARDING_STEP:
      draft.splice(action.index, 1);
      return draft;
    case actionTypes.EDIT_ONBOARDING_STEP:
      draft[action.index] = { ...draft[action.index], ...action.step };
      return draft;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ONBOARDING_STEPS;
  }
});

const DEFAULT_ONBOARDING_CURRENT_STEP = ONBOARDING_STEP_NAME.WELCOME;
const onboardingCurrentStepReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_ONBOARDING_CURRENT_STEP;
  switch (action.type) {
    case actionTypes.SET_CURRENT_ONBOARDING_STEP:
      draft = action.currentStep;
      return draft;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ONBOARDING_CURRENT_STEP;
  }
});

export interface OnboardingForm {
  medications: any[];
  doctorName: string;
  doctorPhone: string;
  email: string;
  phone: string;
  zipCode: string;
  leadAccountId: string;
  isZipCodeDeliverable: boolean | null;
  flow: string;
  isNoRxTransferFlow: boolean;
  isDoctorTransfer: boolean;
  shouldTransferAllMeds: boolean | null;
  error?: JSX.Element;
}

const DEFAULT_ONBOARDING_FORM_STATE: OnboardingForm = {
  medications: [EMPTY_MEDICATION],
  doctorName: '',
  doctorPhone: '',
  email: '',
  phone: '',
  zipCode: '',
  leadAccountId: '',
  isZipCodeDeliverable: null,
  flow: '',
  isNoRxTransferFlow: false,
  isDoctorTransfer: false,
};

const onboardingFormReducer = produce<OnboardingForm>((draft, action) => {
  if (!draft) return DEFAULT_ONBOARDING_FORM_STATE;
  switch (action.type) {
    case actionTypes.SET_ONBOARDING_VALUES:
      draft = { ...draft, ...action.values };
      return draft;
    case actionTypes.SET_ONBOARDING_FLOW:
      draft.flow = action.flow;
      return draft;
    case actionTypes.HYDRATE_ONBOARDING_STORAGE:
      const { email, zipCode, phone, leadAccountId } = action;
      draft = { ...draft, email, zipCode, phone, leadAccountId };
      return draft;
    case actionTypes.SET_ONBOARDING_MEDICATIONS:
      draft.medications = action.medications;
      return draft;
    case actionTypes.SET_ONBOARDING_ERROR:
      draft.error = action.error;
      return draft;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_ONBOARDING_FORM_STATE;
    case actionTypes.SET_SHOULD_TRANSFER_ALL_MEDS:
      draft.shouldTransferAllMeds = action.shouldTransferAllMeds;
      return draft;
    default:
      return draft;
  }
});

const DEFAULT_PHARMACY_SEARCH_STATE = {
  isFetching: false,
  numFetches: 0, // NOTE: Keeps track of the number of outstanding fetch requests to prevent isFetching set to false before all requests are resolved.
  nearbyPlaces: [],
  selectedPharmacy: {},
};

// @ts-ignore
const pharmacySearchReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_PHARMACY_SEARCH_STATE;

  switch (action.type) {
    case actionTypes.SET_IS_FETCHING_NEARBY:
      draft.isFetching = true;
      draft.numFetches += 1;
      break;
    case actionTypes.SET_NEARBY_PLACES:
      const newFetchNum = draft.numFetches > 1 ? draft.numFetches - 1 : 0;
      draft.nearbyPlaces = action.nearbyPlaces;
      draft.numFetches = newFetchNum;
      draft.isFetching = newFetchNum > 0;
      break;
    case actionTypes.SET_SELECTED_PHARMACY:
      draft.selectedPharmacy = action.selectedPharmacy;
      break;
    case actionTypes.RESET_PHARMACY_SEARCH:
      return DEFAULT_PHARMACY_SEARCH_STATE;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_PHARMACY_SEARCH_STATE;
  }
});

const DEFAULT_CHECKOUT_TRANSFER = {
  isCompleted: false,
};

// @ts-ignore
const checkoutTransferReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_CHECKOUT_TRANSFER;

  switch (action.type) {
    case actionTypes.COMPLETE_CHECKOUT_TRANSFER:
      draft.isCompleted = true;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_CHECKOUT_TRANSFER;
  }
});

const DEFAULT_CHECKOUT_CONTACT_INFO = {
  isCompleted: false,
};

// @ts-ignore
const checkoutContactInfoReducer = produce((draft, action: AnyAction) => {
  if (!draft) return DEFAULT_CHECKOUT_CONTACT_INFO;

  switch (action.type) {
    case actionTypes.COMPLETE_CHECKOUT_CONTACT_INFO:
      draft.isCompleted = true;
      break;
    case actionTypes.SESSION_CLEAR_CURRENT_USER_ID:
      return DEFAULT_CHECKOUT_CONTACT_INFO;
  }
});

const reducerDefinition = {
  account: accountReducer,
  accountUpdateStatus: accountUpdateReducer,
  addressValidation: addressValidationReducer,
  archivedMedications: archivedMedicationsReducer,
  bffCart: bffCartReducer,
  bffCartDeliveryMethods: bffCartDeliveryMethodsReducer,
  bffCartDeliveryShippingOptions: bffCartShippingOptionsReducer,
  bffInsurance: bffInsuranceReducer,
  bffOrder: bffOrderReducer,
  bffOrderHistory: bffOrderHistoryReducer,
  bffOTCCategories: bffOTCItemsReducer,
  chat: chatReducer,
  checkout: checkoutReducer,
  checkoutContactInfo: checkoutContactInfoReducer,
  checkoutDeliveryWindows: checkoutDeliveryWindowsReducer,
  checkoutPickupStations: checkoutPickupStationsReducer,
  checkoutTransfer: checkoutTransferReducer,
  checkoutTransferModal: checkoutTransferModalReducer,
  client: clientReducer,
  deliveryAddress: deliveryAddressReducer,
  displayModal: displayModalReducer,
  doctorForms: doctorFormsReducer,
  facility: facilityReducer,
  horizontalProgressBar: horizontalProgressBarReducer,
  insurances: insuranceReducer,
  landingPageModal: landingPageModalReducer,
  location: locationReducer,
  medicalHistoryForm: medicalHistoryFormReducer,
  medicationDetails: medicationDetailsReducer,
  medicationForms: medicationFormsReducer,
  medicationGuides: medicationGuidesReducer,
  medicationLoadingState: medicationLoadingStateReducer,
  medicationManagement: medicationManagementReducer,
  // TODO: Standardize the modal reducers into one modal reducer
  modal: modalReducer,
  networkErrors: networkErrorsReducer,
  onboardingCurrentStep: onboardingCurrentStepReducer,
  onboardingForm: onboardingFormReducer,
  onboardingSteps: onboardingStepsReducer,
  ordersLoading: orderLoadingReducer,
  orderStatus: orderStatusReducer,
  pageAlert: pageAlertReducer,
  patient: patientReducer,
  paymentCards: paymentCardsReducer,
  pharmacySearch: pharmacySearchReducer,
  promoOnboarding: promoOnboardingReducer,
  redirect: redirectReducer,
  requestRefillModalStatus: requestRefillModalStatusReducer,
  resources: resourcesReducer,
  // The `router` key is here for the sake of references to `reducerDefinition`,
  // but be aware it is overwritten when combining the reducers.
  router: connectRouter(HISTORY),
  routerLocation: routerLocationReducer,
  session: sessionReducer,
  setEditAddressState: setEditAddressStateReducer,
  snackBar: SnackBarReducer,
  transferMedications: medicationsApiReducer,
  transferRxDependent: transferRxDependentReducer,
  transfers: transfersReducer,
  ui: uiReducer,
  verifyEmail: verifyEmailReducer,
};

const combinedReducers = (history: History<LocationState>) =>
  combineReducers<typeof reducerDefinition>({
    ...reducerDefinition,
    router: connectRouter(history),
  });

type ReducerState = ReturnType<ReturnType<typeof combinedReducers>>;

export interface ExtendedReducerState extends Omit<ReducerState, 'router'> {
  client: any;
  router: RouterState;
}

const persistConfig: PersistConfig = {
  key: 'consumer-web',
  storage: storage,
  whitelist: [
    'auth',
    'bffCart',
    'checkout',
    'client',
    'displayModal',
    'location',
    'promoOnboarding',
    'routerLocation',
    'session',
    'snackBar',
    'ui',
  ],
  version: REDUX_PERSIST_VERSION,
  migrate: createMigrate(
    {
      // clear out any old persisted states before the product launched
      // this will also be a place where we can upgrade our store version and create a migration script if necessary
      1: () => {
        return {};
      },
      2: () => {
        return {};
      },
      3: () => {
        return {};
      },
      4: () => {
        return {};
      },
    },
    { debug: !IS_PRODUCTION }
  ),
  debug: !IS_PRODUCTION,
};

const rootReducer = (() => {
  // we want to ensure the _persist key is untouched

  const storeKeys = Object.keys(reducerDefinition).concat('_persist', 'client');

  return (state: ExtendedReducerState, action: AnyAction) => {
    if (action.type === REHYDRATE) {
      // when we rehydrate we must ensure that any extra keys are deleted
      // also ensure bad data isn't restored
      const newState = removeBadPersistedCheckoutData(
        update(action.payload, { $keep: storeKeys })
      ) as ExtendedReducerState;
      return newState;
    }

    const newState = combinedReducers(HISTORY)(state, action) as ExtendedReducerState;
    return newState;
  };
})();

const persistedReducer = persistReducer<ExtendedReducerState, AnyAction>(
  persistConfig,
  rootReducer as BaseReducer<ExtendedReducerState, AnyAction>
) as Reducer<ExtendedReducerState, any>;

// eslint-disable-next-line import/no-default-export
export default persistedReducer;
